import { connectorMailReturn } from "./domain/connectorMailReturn";
import { lazy, Suspense, useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { RouteErrorBoundary } from "./components/RouteErrorBoundary";
import { BrandLoadingScreen } from "./components/BrandLoadingScreen";
import { LoginPage } from "./pages/LoginPage";
import { RecoveryPage } from "./pages/RecoveryPage";
import { API_UNAUTHORIZED_EVENT } from "./services/apiTransport";
import { useEms } from "./state/EmsContext";
import { useBrand } from "./state/BrandContext";

const PortfolioPage = lazy(() => import("./pages/PortfolioPage"));
const MapPage = lazy(() => import("./pages/MapPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const AnalysisPage = lazy(() => import("./pages/AnalysisPage"));
const DataExplorerPage = lazy(() => import("./pages/DataExplorerPage"));
const WorkflowPage = lazy(() => import("./pages/WorkflowPage"));
const SitePage = lazy(() => import("./pages/SitePage"));
const ConnectorsPage = lazy(() => import("./pages/ConnectorsPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const AssetDetailPage = lazy(() => import("./pages/AssetDetailPage"));
const MandatesPage = lazy(() => import("./pages/MandatesPage"));
const PublicMandatePage = lazy(() => import("./pages/PublicMandatePage"));

function RouteLoading(): React.ReactNode {
  return (
    <div className="route-loading" role="status" aria-live="polite">
      <span />
      <p>Chargement de la vue…</p>
    </div>
  );
}

function ProtectedApp(): React.ReactNode {
  const { ready, session, role, apiState, apiError, retryApi, logout } =
    useEms();
  const { brand } = useBrand();
  const location = useLocation();
  useEffect(() => {
    const expireSession = (): void => {
      void logout();
    };
    window.addEventListener(API_UNAUTHORIZED_EVENT, expireSession);
    return () => {
      window.removeEventListener(API_UNAUTHORIZED_EVENT, expireSession);
    };
  }, [logout]);
  if (!ready) return <BrandLoadingScreen />;
  if (apiState === "error" || apiState === "forbidden") {
    return (
      <div className="api-state-screen" role="alert">
        <span className="loading-mark">{brand.displayName.slice(0, 1)}.</span>
        <p className="eyebrow">
          {apiState === "forbidden" ? "ACCÈS REFUSÉ" : "SERVICE INDISPONIBLE"}
        </p>
        <h1>
          {apiState === "forbidden"
            ? "Ce périmètre ne vous est pas attribué"
            : "Impossible de charger les données"}
        </h1>
        <p>{apiError}</p>
        <button className="primary-button" onClick={() => void retryApi()}>
          Réessayer
        </button>
      </div>
    );
  }
  if (!session)
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  const operationalRoute = <Navigate to="/app/admin/audit" replace />;
  const isSuperAdmin = role === "super_admin";

  return (
    <AppShell>
      <Suspense fallback={<RouteLoading />}>
        <RouteErrorBoundary key={location.pathname}>
          <Routes>
            <Route
              index
              element={
                <Navigate
                  to={isSuperAdmin ? "admin/audit" : "portfolio"}
                  replace
                />
              }
            />
            <Route
              path="portfolio"
              element={isSuperAdmin ? operationalRoute : <PortfolioPage />}
            />
            <Route
              path="company/:companyId"
              element={isSuperAdmin ? operationalRoute : <AssetDetailPage />}
            />
            <Route
              path="delivery-point/:pointId"
              element={isSuperAdmin ? operationalRoute : <AssetDetailPage />}
            />
            <Route
              path="map"
              element={isSuperAdmin ? operationalRoute : <MapPage />}
            />
            <Route
              path="dashboard"
              element={isSuperAdmin ? operationalRoute : <DashboardPage />}
            />
            <Route
              path="analysis/raw"
              element={isSuperAdmin ? operationalRoute : <DataExplorerPage />}
            />
            <Route
              path="analysis/:view?"
              element={isSuperAdmin ? operationalRoute : <AnalysisPage />}
            />
            <Route
              path="site/:section?"
              element={isSuperAdmin ? operationalRoute : <SitePage />}
            />
            <Route
              path="workflow/:kind"
              element={isSuperAdmin ? operationalRoute : <WorkflowPage />}
            />
            <Route
              path="admin/connectors/:connectorId"
              element={
                isSuperAdmin ? (
                  <ConnectorsPage />
                ) : (
                  <Navigate to="/app/dashboard" replace />
                )
              }
            />
            <Route path="admin/:section" element={<AdminPage />} />
            <Route
              path="mandates"
              element={
                role === "client" || isSuperAdmin ? (
                  <Navigate to="/app/dashboard" replace />
                ) : (
                  <MandatesPage />
                )
              }
            />
            <Route path="profile" element={<ProfilePage />} />
            <Route
              path="*"
              element={
                <Navigate
                  to={isSuperAdmin ? "admin/audit" : "portfolio"}
                  replace
                />
              }
            />
          </Routes>
        </RouteErrorBoundary>
      </Suspense>
    </AppShell>
  );
}

export function App(): React.ReactNode {
  const { ready, session, role } = useEms();
  const location = useLocation();
  return (
    <Routes>
      <Route
        path="/login"
        element={
          ready && session ? (
            <Navigate
              to={connectorMailReturn(location.state, role) ?? "/app"}
              replace
            />
          ) : (
            <LoginPage />
          )
        }
      />
      <Route path="/recovery" element={<RecoveryPage mode="recovery" />} />
      <Route path="/invitation" element={<RecoveryPage mode="invitation" />} />
      <Route
        path="/mandate/:token"
        element={
          <RouteErrorBoundary>
            <Suspense fallback={<BrandLoadingScreen />}>
              <PublicMandatePage />
            </Suspense>
          </RouteErrorBoundary>
        }
      />
      <Route path="/app/*" element={<ProtectedApp />} />
      <Route
        path="*"
        element={<Navigate to={session ? "/app" : "/login"} replace />}
      />
    </Routes>
  );
}
